import { Flex, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import { useGenerateMeetingNote } from '../hooks/useGenerateMeetingNote';
import { useMeetingNotes } from '../hooks/useMeetingNotes';
import { MeetingNoteStepProps } from '../types';

type Props = MeetingNoteStepProps & {
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export function NoteProcessingStep({ data, onSubmittingChange }: Props) {
  const { generateMeetingNote } = useGenerateMeetingNote();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [meetingNoteId, setMeetingNoteId] = useState<string>();
  const didSubmitRef = useRef(false);

  const clientId = data.isFamily
    ? (data.family?.mainFamilyMember.clientId ?? '')
    : (data.client?.id ?? '');
  const { meetingNotes } = useMeetingNotes({ clientId: clientId || undefined });

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const submit = async () => {
    if (!data.selectedRecordingId) {
      notifyAndLogError(
        'No source selected',
        'Please select a source and try again.',
        new Error('Missing recording ID'),
      );
      return;
    }
    if (!data.template && !data.selectedAgendaFileId) {
      notifyAndLogError(
        'No template or agenda selected',
        'Please select a template or agenda and try again.',
        new Error('Missing template/agenda ID'),
      );
      return;
    }

    const notePayload = {
      dataUploadFileId: data.selectedRecordingId,
      templateId: data.template?.id,
      agendaFileId: data.selectedAgendaFileId,
      summarise: data.summarise,
    };

    setIsSubmitting(true);
    try {
      let response;
      if (data.isFamily) {
        if (!data.family) {
          notifyAndLogError(
            'No family selected',
            'Please select a family and try again.',
            new Error('Missing family ID'),
          );
          return;
        }
        response = await generateMeetingNote(data.family.id, 'family', notePayload);
      } else {
        if (!data.client?.id) {
          notifyAndLogError(
            'No client selected',
            'Please select a client and try again.',
            new Error('Missing client ID'),
          );
          return;
        }
        response = await generateMeetingNote(data.client.id, 'client', notePayload);
      }
      setMeetingNoteId(response.data.meetingNoteId);
      dispatchAppEvent('MEETING_NOTE_GENERATED');
    } catch (error) {
      notifyAndLogError('Failed to generate meeting note', 'Please try again.', error);
      setIsSubmitting(false);
    }
  };

  const hasId = () => {
    if (data.isFamily) {
      return !!data.family?.id;
    } else {
      return !!data.client?.id;
    }
  };

  useEffect(() => {
    if (!hasId() || didSubmitRef.current) return;
    didSubmitRef.current = true;
    void submit();
  }, [data]);

  useEffect(() => {
    if (!meetingNoteId || !clientId) return;

    const note = meetingNotes.find(n => n.id === meetingNoteId);
    if (!note?.backgroundProcess) return;

    const status = note.backgroundProcess.status;

    if (status === 'FAILED') {
      setIsSubmitting(false);
      return;
    }

    if (status === 'SUCCESS') {
      setIsSubmitting(false);
    }
  }, [meetingNotes, meetingNoteId, clientId]);

  return (
    <Flex direction="column" flex={1} h="100%" justify="center" align="center">
      <Text c="gray.10" fw={FONT_WEIGHTS.bold} size="displayMd">
        Fine tuning...
      </Text>
    </Flex>
  );
}
